import React, { useEffect, useState } from "react";
import { Box, Typography, IconButton, Tooltip } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { getAllPrompts } from "../../features/promptDetails/promptDetailsAction";
import PaginatedTable from "../Common/Tables/table";
import Dialog from "../Common/Dialog";
import { IconEye, IconInfoCircleFilled } from "@tabler/icons-react";
import request from "../../request/request";
import { useForm } from "react-hook-form";
import * as Yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import ConfirmDialog from "../Common/ConfirmDialog";
import PromptFormDialog from "./PromptForm";
import { getAllSystemPromptsByType } from "../../features/systemPromptDetails/systemPromptDetailsAction";

const Prompt = () => {
  const dispatch = useDispatch();
  const { data, count, loading } = useSelector((state) => state.promptDetail);
  const { data: systemPromptTypeData, loading: systemPromptTypeLoading } =
    useSelector((state) => state.systemPromptTypeDetail);
  const [limit, setLimit] = useState(10);
  const [searchText, setSearchText] = useState("");
  const [pageOffset, setPageOffset] = useState(0);
  const [openAddPromptDialog, setOpenAddPromptDialog] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingPrompt, setEditingPrompt] = useState(null);
  const [openViewDialog, setOpenViewDialog] = useState(false);
  const [openSystemPrompt, setSystemPrompt] = useState(false);
  const [viewingPrompt, setViewingPrompt] = useState(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [promptToDelete, setPromptToDelete] = useState(null);

  const llmModels = [
    { label: "GPT-4o", value: "gpt-4o" },
    { label: "GPT-4.1", value: "gpt-4.1" },
    {
      label: "Claude 3.5 Sonnet",
      value: "anthropic.claude-3-5-sonnet-20240620-v1:0",
    },
  ];

  const categoryOptions = [
    { value: "accounts", label: "Accounts" },
    { value: "contacts", label: "Contacts" },
    { value: "meetings", label: "Meetings" },
    { value: "opportunities", label: "Opportunities" },
  ];

  const systemPromptOptions =
    systemPromptTypeData?.map((item) => ({
      label: item.name,
      value: item.uuid,
    })) || [];

  const fetchPrompts = React.useCallback(
    (params = {}) => {
      const {
        limit: l = limit,
        offset = pageOffset,
        search = searchText,
      } = params;

      const query = {
        limit: l,
        offset,
      };

      if (search?.trim()) {
        query.search = search.trim();
        query.fields = JSON.stringify([
          "tabName",
          "llmModel",
          "limit",
          "useKnowledgeBase",
        ]);
      }

      dispatch(getAllPrompts(query));
    },
    [limit, pageOffset, searchText, dispatch]
  );

  useEffect(() => {
    fetchPrompts();
  }, [limit, pageOffset, searchText, fetchPrompts]);

  // Yup schema
  const schema = Yup.object().shape({
    promptVersion: Yup.string().required("Prompt Name is required"),
    limit: Yup.number()
      .required("Limit is required")
      .positive("Must be a positive number"),
    category: Yup.array()
      .min(1, "At least one category is required")
      .of(
        Yup.object().shape({
          value: Yup.string().required(),
          label: Yup.string().required(),
        })
      ),
    useKnowledgeBase: Yup.boolean(),
    llmModel: Yup.object({
      value: Yup.string().required(),
      label: Yup.string().required(),
    }).required("LLM Model is required"),
    prompt: Yup.string().required("Prompt Content is required"),
  });

  // React Hook Form setup
  const {
    control,
    handleSubmit,
    reset,
    // setValue,
    watch,
    // formState,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      promptVersion: "",
      limit: 900,
      category: [],
      useKnowledgeBase: false,
      llmModel: "gpt-4o",
      prompt: "",
    },
  });

  const watchedCategory = watch("category");

  useEffect(() => {
    if (watchedCategory && watchedCategory.length > 0) {
      const types = watchedCategory.map((cat) =>
        typeof cat === "string" ? cat : cat.value
      );

      dispatch(getAllSystemPromptsByType({ type: JSON.stringify(types) }));
    }
  }, [watchedCategory, dispatch]);

  // const fillFormForEdit = (row) => {
  //   const resolvedCategories = Array.isArray(row.category)
  //     ? row.category
  //     : [row.category].filter(Boolean);

  //   const normalizedCategories = resolvedCategories.map((cat) => {
  //     if (typeof cat === "object" && cat !== null) return cat;
  //     const matched = categoryOptions.find((opt) => opt.value === cat);
  //     return matched || { value: cat, label: cat };
  //   });

  //   const selectedLLM = llmModels.find((m) => m.value === row.llmModel);

  //   const selectedSystemPrompt =
  //     systemPromptOptions.find((opt) => opt.value === row.systemPrompt) || null;

  //   reset({
  //     promptVersion: row.promptVersion || "",
  //     limit: row.limit || 900,
  //     category: normalizedCategories,
  //     useKnowledgeBase: row.useKnowledgeBase || false,
  //     llmModel: selectedLLM || null,
  //     prompt: row.prompt || "",
  //     systemPrompt: selectedSystemPrompt,
  //   });
  // };

  const onSubmit = async (values) => {
    try {
      const payload = {
        ...values,
        llmModel: values.llmModel?.value || "",
        category: values.category.map((c) =>
          typeof c === "string" ? c : c.value
        ),
        systemPrompt: values.systemPrompt?.value || "",
      };

      const endpoint = isEditMode
        ? `/api/v1/prompt-management/update/${editingPrompt?.uuid}`
        : "/api/v1/prompt-management/create";

      const method = isEditMode ? "PUT" : "POST";

      const response = await request(endpoint, {
        method,
        body: payload,
        auth: true,
      });

      console.log(
        `Prompt ${isEditMode ? "updated" : "created"} successfully`,
        response
      );
      fetchPrompts();
      handleCloseFormDialog();
    } catch (error) {
      console.error(
        `Error ${isEditMode ? "updating" : "creating"} prompt:`,
        error
      );
    }
  };

  // const handleAddPrompt = () => {
  //   reset({
  //     promptVersion: "",
  //     limit: 900,
  //     category: [],
  //     useKnowledgeBase: false,
  //     llmModel: llmModels.find((m) => m.value === "gpt-4o") || null,
  //     prompt: "",
  //     systemPrompt: null,
  //   });
  //   setIsEditMode(false);
  //   setEditingPrompt(null);
  //   setOpenAddPromptDialog(true);
  // };

  const handleCloseFormDialog = () => {
    reset();
    setOpenAddPromptDialog(false);
    setIsEditMode(false);
    setEditingPrompt(null);
  };

  // const handleEditPrompt = (row) => {
  //   setEditingPrompt(row);
  //   setIsEditMode(true);
  //   fillFormForEdit(row);
  //   setOpenAddPromptDialog(true);
  // };

  // const handleDeleteClick = (row) => {
  //   setPromptToDelete(row);
  //   setShowDeleteDialog(true);
  // };

  const handleConfirmDelete = async () => {
    try {
      const response = await request(
        `/api/v1/prompt-management/delete/${promptToDelete?.uuid}`,
        {
          method: "DELETE",
          auth: true,
        }
      );
      console.log("Prompt deleted successfully", response);
      fetchPrompts();
    } catch (error) {
      console.error("Error deleting prompt:", error);
    } finally {
      setShowDeleteDialog(false);
      setPromptToDelete(null);
    }
  };

  const handleViewPrompt = (row) => {
    setViewingPrompt(row);
    setOpenViewDialog(true);
  };

  const handleCloseViewDialog = () => {
    setOpenViewDialog(false);
    setViewingPrompt(null);
  };

  const handleSystemPrompt = (row) => {
    setViewingPrompt(row);
    setSystemPrompt(true);
  };

  const handleCloseSystemDialog = () => {
    setSystemPrompt(false);
    setViewingPrompt(null);
  };

  const columns = [
    { Header: "Label", accessor: "tabName" },
    { Header: "Type", accessor: "type" },
    { Header: "llmModel", accessor: "llmModel" },
    { Header: "Limit", accessor: "limit" },
    {
      Header: "Use Knowledge Base",
      accessor: "useKnowledgeBase",
      Cell: ({ value }) => (value ? "true" : "false"),
    },
    { Header: "Created", accessor: "userName" },
    {
      Header: "Created On",
      accessor: "createdAt",
      Cell: ({ value }) => new Date(value).toLocaleString(),
    },
    {
      Header: "Updated On",
      accessor: "updatedAt",
      Cell: ({ value }) => new Date(value).toLocaleString(),
    },

    {
      Header: "Actions",
      accessor: "actions",
      disableSortBy: true,
      Cell: ({ row }) => (
        <Box sx={{ display: "flex", gap: 1 }}>
          <Tooltip title="View Prompt">
            <IconButton
              color="info"
              onClick={() => handleViewPrompt(row.original)}
            >
              <IconEye size={"1.8rem"} />
            </IconButton>
          </Tooltip>
          <Tooltip title="View System Prompt">
            <IconButton
              color="info"
              onClick={() => handleSystemPrompt(row.original)}
            >
              <IconInfoCircleFilled size={"1.8rem"} />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ width: "100%", padding: "10px", marginTop: "65px" }}>
      <PaginatedTable
        columns={columns}
        data={data || []}
        totalCount={count || 0}
        heading="Prompt List"
        hideFeatureButton
        paginated={true}
        pageOffset={pageOffset}
        limit={limit}
        isFetching={loading}
        setLimitFromParents={setLimit}
        setPageOffsetFromParents={setPageOffset}
        searchText={searchText}
        setSearchText={(text) => {
          setSearchText(text);
          setPageOffset(0);
        }}
        showSearch={true}
      />

      <PromptFormDialog
        open={openAddPromptDialog}
        isEditMode={isEditMode}
        control={control}
        handleClose={handleCloseFormDialog}
        handleSubmit={handleSubmit}
        onSubmit={onSubmit}
        llmModels={llmModels}
        categoryOptions={categoryOptions}
        systemPromptOptions={systemPromptOptions}
        systemPromptTypeLoading={systemPromptTypeLoading}
      />
      <Dialog
        isOpen={openViewDialog}
        handleClose={handleCloseViewDialog}
        cancelSubmitHandle={handleCloseViewDialog}
        hideOkBtn
        title={<Typography variant="h6">Prompt Content</Typography>}
      >
        {viewingPrompt && (
          <Box
            sx={{
              padding: 2,
              border: "1px solid #ccc",
              borderRadius: "8px",
              backgroundColor: "#f9f9f9",
            }}
            dangerouslySetInnerHTML={{ __html: viewingPrompt.prompt }}
          />
        )}
      </Dialog>

      <Dialog
        isOpen={openSystemPrompt}
        handleClose={handleCloseSystemDialog}
        cancelSubmitHandle={handleCloseSystemDialog}
        hideOkBtn
        title={<Typography variant="h6">System Prompt Content</Typography>}
      >
        {viewingPrompt && (
          <Box
            sx={{
              padding: 2,
              border: "1px solid #ccc",
              borderRadius: "8px",
              backgroundColor: "#f9f9f9",
            }}
            dangerouslySetInnerHTML={{ __html: viewingPrompt.systemPrompt }}
          />
        )}
      </Dialog>

      <ConfirmDialog
        open={showDeleteDialog}
        title={`Are you sure you want to delete "${promptToDelete?.promptVersion}"?`}
        onConfirm={handleConfirmDelete}
        onCancel={() => setShowDeleteDialog(false)}
      />
    </Box>
  );
};

export default Prompt;
